// ─── ⭐⭐ DALLE FASCE ALLE CASELLE, E RITORNO ────────────────────────────────
//
// 🗣️ Chiesto da lui il 18/09/2026, guardando le Fasce prenotabili: *«è possibile
// replicare la stessa modalità di inserimento che abbiamo fatto per i listini
// qui nelle fasce?»* — e poi, sulle fasce spente: *«mi dà la possibilità di
// accendere e spegnere le fasce orarie spente con un solo clic, anziché doverle
// ogni volta ripianificare»*.
//
// 🚨⭐⭐ **LA DIFFERENZA COL LISTINO, ed è quella che decide tutto il resto.**
// Nel listino una casella **È** una riga: una mezz'ora, un prezzo, una riga nel
// database. Qui no — una fascia è una **banda** (12:30→15:30), quindi N caselle
// si fondono in UNA riga e una riga si spalma su N caselle.
// ⇒ Da cui due funzioni invece di nessuna, e una rotta che riscrive **il
//   giorno** invece di scrivere le caselle una per una.
//
// ⛔ **E LE TRE FACCE DI UNA CASELLA NON SONO DUE**: vuota (nessuna fascia),
// accesa (si prenota, con il suo passo), **spenta** (la fascia c'è e il passo
// è conservato, ma i soci non ci entrano). Per chi prenota le ultime due si
// somigliano — non si prenota né sull'una né sull'altra — ma non sono la stessa
// cosa: la spenta **ricorda**, ed è tutto il motivo per cui lui l'ha voluta.
// 📌 *Cancellare e ridisegnare è lo stesso gesto di spegnere e riaccendere solo
//    per chi non deve rifarlo.*
/** Il passo più fine che la griglia sa disegnare: una riga = mezz'ora. */
export const PASSO_CASELLA = 30;
function minuti(ora) {
    const [h, m] = ora.split(':');
    return Number(h) * 60 + Number(m);
}
function ora(min) {
    return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}
/**
 * ⚖️ QUALI FASCE LA GRIGLIA SA DISEGNARE, e quali no.
 *
 * Una banda si disegna se i suoi estremi cadono sulla mezz'ora: le righe sono
 * quelle, e una fascia 09:15–10:45 non ha caselle a cui appartenere.
 *
 * ⛔ **Una che non ci sta NON si arrotonda alla mezz'ora più vicina**: sarebbero
 * ore vere spostate senza che nessun errore lo dica. Resta nell'elenco, dove si
 * vede e si modifica a mano — ed è la schermata a doverlo dire, non questo file
 * a nasconderlo.
 */
export function laGrigliaLaSaDisegnare(f) {
    return minuti(f.startsAtTime) % PASSO_CASELLA === 0 && minuti(f.endsAtTime) % PASSO_CASELLA === 0;
}
/**
 * Le fasce diventano caselle: ogni banda colora le sue mezz'ore.
 *
 * ⚠️ Si guardano SOLO le fasce che la griglia sa disegnare, e solo quelle
 * dell'ambito chiesto: mescolare due ambiti darebbe a una casella due valori, e
 * la griglia ne sa mostrare uno.
 *
 * 🚨 Una casella accesa **vince** su una spenta che stia sotto: il database
 * permette a una spenta di stare sotto una attiva (il vincolo di
 * non-sovrapposizione è `where (active)`), e in quel caso la verità che conta
 * per chi prenota è quella accesa.
 */
export function caselleDalleFasce(fasce) {
    const dentro = new Map();
    for (const f of fasce) {
        if (!laGrigliaLaSaDisegnare(f))
            continue;
        for (let t = minuti(f.startsAtTime); t < minuti(f.endsAtTime); t += PASSO_CASELLA) {
            const chiave = `${f.weekday}|${ora(t)}`;
            const gia = dentro.get(chiave);
            if (gia !== undefined && (gia.active || !f.active))
                continue;
            dentro.set(chiave, { weekday: f.weekday, time: ora(t), slotMinutes: f.slotMinutes, active: f.active });
        }
    }
    return [...dentro.values()];
}
/**
 * ⭐⭐ E le caselle tornano bande: ogni **corsa** di caselle attaccate, con lo
 * stesso passo e lo stesso stato, è una riga.
 *
 * ⚖️ **La banda comincia dove comincia il colore**, ed è una scelta da
 * dichiarare. Gli orari in cui si può entrare nascono da lì (inizio + passo,
 * + passo…), quindi due bande da novanta che si toccano e diventano una sola
 * danno gli stessi inizi **se la prima è lunga un multiplo del suo passo** —
 * che è il caso del circolo vero (09:30–11:00 e 11:00–12:30). Se non lo fosse,
 * gli inizi si sposterebbero: per questo la schermata **disegna le caselle
 * d'inizio**, così quello che cambia si vede invece di doverlo dedurre.
 * 📌 *Una regola che si vede non ha bisogno di essere ricordata.*
 */
export function bandeDalleCaselle(caselle) {
    const out = [];
    const perGiorno = new Map();
    for (const c of caselle) {
        const elenco = perGiorno.get(c.weekday) ?? [];
        elenco.push(c);
        perGiorno.set(c.weekday, elenco);
    }
    for (const [weekday, elenco] of [...perGiorno.entries()].sort((a, b) => a[0] - b[0])) {
        const ordinate = [...elenco].sort((a, b) => minuti(a.time) - minuti(b.time));
        let corsa = null;
        for (const c of ordinate) {
            const t = minuti(c.time);
            const attacca = corsa !== null &&
                minuti(corsa.endsAtTime) === t &&
                corsa.slotMinutes === c.slotMinutes &&
                corsa.active === c.active;
            if (attacca) {
                corsa.endsAtTime = ora(t + PASSO_CASELLA);
                continue;
            }
            if (corsa !== null)
                out.push(corsa);
            corsa = {
                weekday,
                startsAtTime: c.time,
                endsAtTime: ora(t + PASSO_CASELLA),
                slotMinutes: c.slotMinutes,
                active: c.active,
            };
        }
        if (corsa !== null)
            out.push(corsa);
    }
    return out;
}
/**
 * Gli orari in cui si COMINCIA dentro una banda, come li mostra la griglia.
 *
 * 🚨 È la stessa regola di `passiDelGiorno` — un passo entra solo se ci sta
 * **intero** — e sta scritta due volte apposta? No: quella là risponde per un
 * giorno e un campo, guardando validità e ambiti; questa risponde **per una
 * banda disegnata**, che è quello che la schermata deve colorare mentre lui la
 * sta ancora scrivendo e nel database non c'è niente.
 * ⚠️ Se un domani le due dovessero dare risposte diverse, quella buona è
 * `passiDelGiorno`: è lei a decidere cosa il socio può prenotare davvero.
 */
export function iniziDellaBanda(b) {
    const out = [];
    const fine = minuti(b.endsAtTime);
    for (let t = minuti(b.startsAtTime); t + b.slotMinutes <= fine; t += b.slotMinutes)
        out.push(ora(t));
    return out;
}
/**
 * ⭐⭐ LE CASELLE, CIASCUNA COL SUO POSTO DENTRO LO SLOT.
 *
 * 🗣️ Nasce dalla sua scelta del 18/09/2026, davanti a cinque proposte disegnate:
 * *«un riquadro alto per slot, e dentro c'è scritto da che ora a che ora»*. E
 * dalla ragione per cui l'aveva chiesto: *«ci ho messo un po' a capire che
 * quella barretta verde sulla sinistra fosse l'inizio dello slot da 90»*.
 *
 * ⚖️ **Perché sta qui e non nella schermata**: è la stessa domanda a cui
 * risponde `iniziDellaBanda` — dove comincia uno slot — solo portata fino in
 * fondo, cioè *fin dove arriva*. Scritta nella schermata, il giorno in cui un
 * elenco o una stampa vorranno dire la stessa cosa ne nascerebbe una seconda
 * copia, e due copie divergono sul caso storto (che qui è il `resto`).
 */
export function caselleNeiLoroSlot(bande) {
    const out = [];
    for (const b of bande) {
        const coperte = new Set();
        for (const inizio of iniziDellaBanda(b)) {
            const da = minuti(inizio);
            const a = da + b.slotMinutes;
            for (let t = da; t < a; t += PASSO_CASELLA) {
                coperte.add(ora(t));
                const ultima = t + PASSO_CASELLA >= a;
                // ⚠️ Una casella che apre E chiude — il passo da 30 — resta `apre`: è
                // un blocco di una riga sola, e non ha né mezzo né fondo.
                const posto = t === da ? 'apre' : ultima ? 'chiude' : 'dentro';
                out.push({ weekday: b.weekday, time: ora(t), posto, da: inizio, a: ora(a) });
            }
        }
        for (let t = minuti(b.startsAtTime); t < minuti(b.endsAtTime); t += PASSO_CASELLA) {
            if (coperte.has(ora(t)))
                continue;
            out.push({ weekday: b.weekday, time: ora(t), posto: 'resto', da: null, a: null });
        }
    }
    return out;
}
//# sourceMappingURL=fasce-a-griglia.js.map